import { useRef } from "react";
import { useTranslation } from "react-i18next";
import type { MessageInstance } from "antd/es/message/interface";
import {
  getNextTableName,
  type BillOut,
  type NextTableName,
  type TableReservationNotice,
} from "../../../../../api/bills";
import { isCloudUnreachableError } from "../../../../../api/client";
import { getApiErrorMessage } from "../../../../../utils/translations";
import type { FreeTableOffer } from "./useReservationConfirm";

interface OpenNextTableParams {
  bills: BillOut[];
  personalNumberingEnabled: boolean;
  currentUserId: number | null;
  message: MessageInstance;
  confirmNotice: (tableName: string, notice: TableReservationNotice) => Promise<boolean>;
  offerFreeTable: (reservedName: string, afterSearch?: () => void) => Promise<FreeTableOffer>;
  /** Отваря екрана за поръчка на масата с това име (`handleNewTableSubmit`). */
  onOpen: (tableName: string) => void;
}

/**
 * Бързото отваряне при „Бързо закриване" (Конфигурация → Маркиране): „Нова
 * маса" без модал за име — най-малкият свободен положителен номер сред
 * ОТВОРЕНИТЕ маси. Номерът идва от BE-то (GET /bills/next-table-name), защото
 * сервитьор без view_all_users_bills не вижда чуждите маси, а при изключена
 * „обща номерация" номерът трябва да е свободен глобално; при включена BE-то
 * брои само собствените му маси.
 *
 * Резервираният номер се пита („масата има резервация — продължаваш ли?");
 * „Отказ" предлага следващия свободен номер без резервация (`offerFreeTable`).
 * Връща функцията за натискането на „Нова маса".
 */
export const useOpenNextTable = ({
  bills,
  personalNumberingEnabled,
  currentUserId,
  message,
  confirmNotice,
  offerFreeTable,
  onOpen,
}: OpenNextTableParams) => {
  const { t } = useTranslation();
  // Потокът е дълъг (питане + до 20 проверки без модал) — второ натискане/F5
  // междувременно не пуска втори паралелен поток (двете питания биха се
  // „отказвали" взаимно, `useReservationConfirm.ask`).
  const openingTableRef = useRef(false);

  const openNextTable = async () => {
    let next: NextTableName;
    try {
      next = await getNextTableName();
    } catch (err: unknown) {
      // Fallback при стар BE без endpoint-а: локална сметка от видимите маси
      // (заети "1" и "3" → "2"; при лична номерация чужда маса не брои).
      // Масата СЕ ОТВАРЯ — това е нарочно и остава.
      //
      // Мълчанието обаче беше дефект: `catch {}` даваше НА ЧОВЕКА ДРУГ
      // НОМЕР, без дума. Този номер е сметнат само от масите, които ТОЗИ
      // терминал вижда — при спрял интернет списъкът е от кеша и номерът
      // може да е зает от колега. Затова сега се казва откъде е.
      const used = new Set<number>();
      for (const bill of bills) {
        if (bill.status === "closed") continue;
        if (personalNumberingEnabled && bill.waiter_id !== currentUserId) continue;
        const number = Number(bill.name);
        if (Number.isInteger(number) && number > 0) used.add(number);
      }
      let number = 1;
      while (used.has(number)) number += 1;
      message.warning(
        isCloudUnreachableError(err)
          ? t("ordering.nextTableNameOffline", { name: String(number) })
          : t("ordering.nextTableNameLocal", {
              name: String(number),
              reason: getApiErrorMessage(err, t("common.error")),
            }),
      );
      onOpen(String(number));
      return;
    }
    // Резервираният номер се пита; „Отказ" не отваря нищо. Резервацията идва
    // в самия отговор на next-table-name — без втора заявка check-table-name
    // (липсващо поле → null → без модал). Резервният локален номер отгоре не
    // се пита — облакът тогава не отговаря.
    const proceed = next.reservation ? await confirmNotice(next.name, next.reservation) : true;
    if (proceed) {
      onOpen(next.name);
      return;
    }
    // „Отказ": предлага следващия свободен номер без резервация (същият
    // модал, „Отвори маса N"); нищо свободно → само съобщение. Проверките
    // траят — loading toast, който пада преди модала/съобщението.
    const searchingKey = "reservation-free-table";
    message.loading({
      key: searchingKey,
      content: t("ordering.reservationNotice.searchingFreeTable"),
      duration: 0,
    });
    const offer = await offerFreeTable(next.name, () => message.destroy(searchingKey));
    if (offer.kind === "open") onOpen(offer.name);
    else if (offer.kind === "none") message.warning(t("ordering.reservationNotice.noFreeTable"));
  };

  return async () => {
    if (openingTableRef.current) return;
    openingTableRef.current = true;
    try {
      await openNextTable();
    } finally {
      openingTableRef.current = false;
    }
  };
};
